import React, { Component } from "react";
import { Row, Col, Label, FormGroup } from "reactstrap";
import { AppBadge, AppCard, PageLayout, StatusBadge } from "../../components/ui";

import { OperatorDataModel, UpdateUserPhotoRequest, UserTypeRelation } from "../../models/User";
import { InputText } from "primereact/inputtext";

import { toastService } from "../../services/toastService";
import { SelectItem } from "../../models/SelectItem";
import { utils } from "../../utils/utils";
import { ProfileImage } from "../../components/Image/ProfileImage";
import { fileService } from "../../services/FileService";
import { Merchant, MerchantGroup } from "../../models/MerchantModel";

import _ from "lodash";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { RoleModel } from "../../models/Role";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { UserCog, Check } from "lucide-react";
import { storageService } from "../../services/StorageService";
import { operatorsApiService } from "../../services/api/users/OperatorApiService";
import { Tooltip } from "primereact/tooltip";
import { AppConstants, Image_File_Size } from "../../models/AppConstants";
import { userApiService } from "../../services/api/users/UsersApiService";
import imageCompression from "browser-image-compression";

interface ProfileProps {
    user: OperatorDataModel;
    merchants: Merchant[];
    roles: RoleModel[];
    showMerchant: boolean;
    showRoles: boolean;
    showPassword: boolean;
    // TNPDEVAPPS-1812: show the admin-only "Allow cross-merchant operations" opt-in (create/update operator
    // screens). Omitted on the operator's own-profile screen, where the flag cannot be self-edited.
    showCrossMerchantOption?: boolean;
    allowResetPhoneAndMobile: boolean;
    isEditMode: boolean;
    onMerchantChange?: (merchantId: number) => void;
    onUpdate: (user: OperatorDataModel) => void;
    onSubmit: () => void;
    userType?: UserTypeRelation;
}
const Roles = {
    SUPER_ADMIN: "Super Admin",
    TennAdmin: "10n Admin",
    ComplianceOfficer: "Compliance Officer",
    ComplianceOfficerApprover: "Compliance Officer Approver",
    // The "Individual" role is a row-level-security scope marker for end-customer
    // accounts (see tenn-pay-crm AuthorizingDbContextInterceptor). It must never be
    // assigned to an operator, so it is hidden from the operator role dropdown.
    Individual: "Individual",
};
// TNPDEVAPPS-1812: FlexCube branch that identifies a Tenn Payments (10n) merchant, whose operators may be
// granted cross-merchant access.
const TennPaymentFlexCubeBranch = "TNP";
enum UserStatus {
    Active = 1,
    Inactive = 2,
}
interface ProfileState {
    isLoading: boolean;
    showUserRoles: boolean;
    allowResetPhoneAndMobile: boolean;
    allowDisableUser: boolean;
    isSuperAdmin: boolean;
    userRoles: RoleModel[];
    photoUploading: boolean;
}

export default class ProfileComponent extends Component<ProfileProps, ProfileState> {
    constructor(props: ProfileProps) {
        super(props);

        this.state = {
            isLoading: false,
            showUserRoles: false,
            allowResetPhoneAndMobile: false,
            allowDisableUser: false,
            isSuperAdmin: false,
            userRoles: [],
            photoUploading: false,
        };
    }

    componentDidMount(): void {
        this.getUserRoles();
        this.loadUserRoles();
        this.loadImage();
    }

    getUserRoles = async () => {
        const roles = storageService.getRoles();
        const superadmin = roles.includes(Roles.SUPER_ADMIN);

        this.setState({ isLoading: false, allowDisableUser: superadmin, isSuperAdmin: superadmin });
    };
    loadUserRoles = async () => {
        // TNPDEVAPPS-1812: "10n Admin" is managed solely via the cross-merchant checkbox and is never shown
        // as a role for an operator (roles list, dropdown, or the User Administration grid).
        const filteredRoles = this.props.roles.filter(
            (role) => this.props.user.userRoles.includes(role.id) && role.name !== Roles.TennAdmin,
        );
        this.setState({ isLoading: false, userRoles: filteredRoles, showUserRoles: true });
    };

    loadImage = async () => {
        if (this.props.user?.photoStorageId == null) {
            return;
        }
        const photoResult = await fileService.GetFile(this.props.user?.photoStorageId);

        if (!photoResult.success) {
            toastService.error("Could not load User Photo. Please contact your administrator");
            return;
        }

        const user = this.props.user;
        user.photo = photoResult.data;

        this.props.onUpdate(user);
    };

    getSelectMerchantGroupItems = (options: MerchantGroup[]): SelectItem<number>[] => {
        return options.map(
            (data) =>
                ({
                    label: data.name,
                    value: data.id,
                }) as SelectItem<number>,
        );
    };

    getVerificationClassName = (param: boolean): string => {
        return param === true ? "success" : "danger";
    };

    updateValue = (fieldName: string, value: string) => {
        const user = this.props.user;

        (user as any)[fieldName] = value;

        this.props.onUpdate(user);
    };

    showToast = (message: string) => {
        toastService.success(message, "Action Completed");
    };

    onImageSelected = async (image: File) => {
        if (!utils.isValidImage(image)) {
            toastService.error("Format not supported! Only Image files are allowed.", "File Format");
            return false;
        }

        if (image.size > Image_File_Size) {
            toastService.error("Files less than 5MB are supported only.", "File Size");
            return false;
        }
        const compressedImage = await imageCompression(image, { maxSizeMB: Image_File_Size, useWebWorker: true });
        const base64Image = await utils.convertFileToBase64(compressedImage);
        const createuserPhoto = { ...this.props.user, photo: base64Image as string };
        this.props.onUpdate(createuserPhoto);

        if (this.props.isEditMode) {
            this.setState({ photoUploading: true });

            const request: UpdateUserPhotoRequest = {
                photo: base64Image as string,
                userId: this.props.user.id ?? 0,
                userType: UserTypeRelation.Operator,
            };

            const uploadResult = await userApiService.updateUserPhoto(request);

            if (!uploadResult.success) {
                this.setState({ photoUploading: false });
                toastService.error("Could not upload the image. Please try again.", "Upload Failed");
                return;
            }

            // Update user details with the uploaded image ID
            const updatedUser = { ...this.props.user, photoStorageId: uploadResult.data };
            this.props.onUpdate(updatedUser);

            this.setState({ photoUploading: false });
            toastService.success("Profile image updated successfully.");
        }
    };

    fileInput = (input: boolean) => {
        if (!input) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            return;
        }
    };

    getImageSource = (): string => {
        if (!this.props.user?.photo) {
            return this.defaultImage();
        }
        return utils.appendBase64Data(this.props.user.photo!);
    };

    defaultImage = () => {
        return "";
    };

    onFieldUpdate = (fieldName: string, value: any) => {
        const userData = this.props.user;
        (userData as any)[fieldName] = value;
        this.props.onUpdate(userData!);
    };

    // TNPDEVAPPS-1812: the "Allow cross-merchant operations" flag is only meaningful for a Tenn Payments
    // ("TNP") operator, so the checkbox is shown only when the operator's merchant is a TNP merchant.
    // Create mode reads the branch from the selected merchant in the dropdown; edit mode (no merchants
    // list) reads the branch already resolved on the operator record. The flag is enforced server-side
    // regardless — this is a UI convenience, not the authorization boundary.
    isTennPaymentMerchantSelected = (): boolean => {
        const selectedMerchant = this.props.merchants.find((m) => m.id === Number(this.props.user.merchantId));
        const flexCubeBranch = selectedMerchant?.flexCubeBranch ?? this.props.user.flexCubeBranch;

        return flexCubeBranch === TennPaymentFlexCubeBranch;
    };

    // TNPDEVAPPS-1812: id of the "10n Admin" role for this merchant (present in the loaded role list when
    // available — effectively Tenn Payments). Undefined when the role isn't assignable for the merchant.
    getTennAdminRoleId = (): number | undefined => this.props.roles.find((role) => role.name === Roles.TennAdmin)?.id;

    // TNPDEVAPPS-1812: the checkbox is the ONLY control for cross-merchant access. It sets the flag; the
    // backend reconciles the "10n Admin" role from that flag (assign/remove). The role itself is never
    // shown or edited directly in the portal.
    setCrossMerchant = (enabled: boolean) => {
        const user = this.props.user;
        user.allowedCrossMerchantOperations = enabled;
        this.props.onUpdate(user);
    };

    onRolesChange = (selectedRoleIds: number[]) => {
        const user = this.props.user;
        // "10n Admin" is excluded from the multiselect, so it is never in selectedRoleIds. The backend keeps
        // it independent of this list (it is filtered out of the role sync), driven only by the checkbox.
        user.userRoles = selectedRoleIds;
        this.props.onUpdate(user);
    };

    deactivateOperator = async (id: number) => {
        this.setState({ isLoading: true });
        const result = await operatorsApiService.deactivateoperator(id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }
        const userProfileResult = await operatorsApiService.getById(this.props.user.id!);

        if (!userProfileResult.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfileResult.message);
            return;
        }
        const updatedUser = userProfileResult.data;
        this.setState({ isLoading: false });
        this.props.onUpdate(updatedUser);
        toastService.success("Account Deactivated.", "Action Completed");
    };

    activateoperator = async (id: number) => {
        this.setState({ isLoading: true });
        console.log(id);
        const result = await operatorsApiService.activateoperator(id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }
        const userProfileResult = await operatorsApiService.getById(this.props.user.id!);

        if (!userProfileResult.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfileResult.message);
            return;
        }
        const updatedUser = userProfileResult.data;
        this.setState({ isLoading: false });
        this.props.onUpdate(updatedUser);
        toastService.success("Account Activated.", "Action Completed");
    };
    render() {
        return (
            <PageLayout heading="User Profile" icon={UserCog}>
                <div className="space-y-4">
                    <AppCard>
                                    <AppCard.Header title="User Details" />
                                    <AppCard.Body>
                                                    <Row>
                                                        <Col md="3" lg="3" sm="12" className="p-8 text-center">
                                                            <ProfileImage
                                                                imageStyle={{ width: "100%", maxHeight: "250px" }}
                                                                className="rounded-circle"
                                                                src={this.getImageSource()}
                                                                alt=""
                                                                onImageSelected={this.onImageSelected}
                                                                fileInput={this.fileInput}
                                                                photoUploading={this.state.photoUploading}
                                                            />
                                                        </Col>
                                                        <Col md={9} lg={9} sm="12">
                                                            <Row>
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label for="firstName">First Name</Label>
                                                                        <InputText
                                                                            value={this.props.user.firstName}
                                                                            className={"form-control"}
                                                                            id="firstName"
                                                                            onChange={(e) => this.onFieldUpdate("firstName", e.target.value)}
                                                                            disabled={!this.state.isSuperAdmin}
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label for="lastName">Last Name</Label>
                                                                        <InputText
                                                                            value={this.props.user.lastName}
                                                                            className={"form-control"}
                                                                            id="lastName"
                                                                            onChange={(e) => this.onFieldUpdate("lastName", e.target.value)}
                                                                            disabled={!this.state.isSuperAdmin}
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label for="email">Email Address</Label>
                                                                        <InputText
                                                                            value={this.props.user.email}
                                                                            className={"form-control"}
                                                                            type="email"
                                                                            name="email"
                                                                            id="email"
                                                                            onChange={(e) => this.onFieldUpdate("email", e.target.value)}
                                                                            disabled={!this.state.isSuperAdmin}
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col
                                                                    md={3}
                                                                    className="flex items-center pt-4 px-2.5"
                                                                >
                                                                    <AppBadge variant={this.getVerificationClassName(this.props.user.isEmailVerified) as any}>
                                                                        {this.props.user.isEmailVerified ? "Verified" : "Not Verified"}
                                                                    </AppBadge>
                                                                </Col>
                                                                {!this.props.allowResetPhoneAndMobile && this.props.isEditMode && (
                                                                    <Col md={3} className="pt-8 px-2.5">
                                                                        <a
                                                                            href="#"
                                                                            onClick={() =>
                                                                                this.showToast("Email to Reset Email Address Sent Successfully")
                                                                            }
                                                                        >
                                                                            Reset Email Address
                                                                        </a>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                            <Row>
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label for="mobile">Mobile</Label>
                                                                        <InputText
                                                                            value={this.props.user.phoneNumber}
                                                                            className={"form-control"}
                                                                            type="text"
                                                                            name="phoneNumber"
                                                                            id="phoneNumber"
                                                                            onChange={(e) => this.onFieldUpdate("phoneNumber", e.target.value)}
                                                                            disabled={!this.state.isSuperAdmin}
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col
                                                                    md={3}
                                                                    className="flex items-center pt-4 px-2.5"
                                                                >
                                                                    <AppBadge variant={this.getVerificationClassName(this.props.user.isPhoneNumberVerified) as any}>
                                                                        {this.props.user.isPhoneNumberVerified === true ? "Verified" : "Not Verified"}
                                                                    </AppBadge>
                                                                </Col>
                                                                <Col md={3} className="pt-8 px-2.5">
                                                                    {!this.props.allowResetPhoneAndMobile && this.props.isEditMode && (
                                                                        <a
                                                                            href="#"
                                                                            onClick={() =>
                                                                                this.showToast("Email to Reset Mobile Phone Sent Successfully")
                                                                            }
                                                                        >
                                                                            Reset Mobile
                                                                        </a>
                                                                    )}
                                                                    <br></br>

                                                                    {this.state.allowDisableUser &&
                                                                        this.props.isEditMode &&
                                                                        this.props.user.status == UserStatus.Active && (
                                                                            <a href="#" onClick={() => this.deactivateOperator(this.props.user.id)}>
                                                                                Deactivate User
                                                                            </a>
                                                                        )}
                                                                    {this.state.allowDisableUser &&
                                                                        this.props.isEditMode &&
                                                                        this.props.user.status == UserStatus.Inactive && (
                                                                            <a href="#" onClick={() => this.activateoperator(this.props.user.id)}>
                                                                                Activate User
                                                                            </a>
                                                                        )}
                                                                </Col>
                                                            </Row>

                                                            <Row>
                                                                {this.props.showMerchant && (
                                                                    <Col md={6}>
                                                                        <FormGroup>
                                                                            <Label for="merchant">Merchant</Label>
                                                                            <div>
                                                                                <Dropdown
                                                                                    className="w-full"
                                                                                    value={this.props.user.merchantId}
                                                                                    options={this.props.merchants}
                                                                                    onChange={(e) => {
                                                                                        this.onFieldUpdate("merchantId", e.target.value);
                                                                                        this.props.onMerchantChange &&
                                                                                            this.props.onMerchantChange(e.target.value as number);
                                                                                    }}
                                                                                    optionLabel="name"
                                                                                    optionValue="id"
                                                                                    filter
                                                                                    showClear
                                                                                    filterBy="name"
                                                                                    placeholder="Select Merchant"
                                                                                />
                                                                            </div>
                                                                        </FormGroup>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                            <Row>
                                                                {this.props.showPassword && (
                                                                    <Col md={6}>
                                                                        <FormGroup>
                                                                            <Label for="password">Password</Label>
                                                                            <div>
                                                                                <InputText
                                                                                    value={this.props.user.password}
                                                                                    className={"form-control"}
                                                                                    type="password"
                                                                                    name="password"
                                                                                    id="password"
                                                                                    onChange={(e) => this.onFieldUpdate("password", e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </FormGroup>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                            {/* TNPDEVAPPS-1812: cross-merchant operations opt-in, only for Tenn Payments ("TNP") operators. */}
                                                            {this.props.showCrossMerchantOption && this.isTennPaymentMerchantSelected() && (
                                                                <Row>
                                                                    <Col md={6}>
                                                                        <FormGroup>
                                                                            <div className="flex items-center gap-2 pt-2">
                                                                                <Checkbox
                                                                                    inputId="allowedCrossMerchantOperations"
                                                                                    checked={!!this.props.user.allowedCrossMerchantOperations}
                                                                                    disabled={!this.state.isSuperAdmin}
                                                                                    onChange={(e) => this.setCrossMerchant(!!e.checked)}
                                                                                />
                                                                                <Label for="allowedCrossMerchantOperations" className="mb-0">
                                                                                    Allow cross-merchant operations
                                                                                </Label>
                                                                            </div>
                                                                        </FormGroup>
                                                                    </Col>
                                                                </Row>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </AppCard.Body>
                                            </AppCard>
                                <AppCard>
                                    <AppCard.Header title="Roles" />
                                    <AppCard.Body>
                                        {this.props.showRoles && this.state.isSuperAdmin && (
                                            <MultiSelect
                                                value={this.props.user.userRoles?.filter((id) => id !== this.getTennAdminRoleId())}
                                                options={this.props.roles.filter((role) => role.name !== Roles.Individual && role.name !== Roles.TennAdmin)}
                                                onChange={(e) => this.onRolesChange(e.value)}
                                                optionLabel="name"
                                                optionValue="id"
                                                placeholder="Select Roles"
                                                filter
                                                className="multiselect-custom mb-4"
                                            />
                                        )}
                                        {this.state.showUserRoles && (
                                            <DataTable loading={this.state.isLoading} value={this.state.userRoles}>
                                                <Column field="name" header="Roles" headerStyle={{ display: "none" }} />
                                            </DataTable>
                                        )}
                                        {this.state.isSuperAdmin && (
                                            <div className="flex justify-end px-5 py-4">
                                                <Button
                                                    type="button"
                                                    label="Submit"
                                                    icon={<Check size={16} />}
                                                    onClick={this.props.onSubmit}
                                                />
                                            </div>
                                        )}
                                    </AppCard.Body>
                                </AppCard>
                </div>
            </PageLayout>
        );
    }
}
